// RoseyCo contact-click tracking — see marketing-ide/docs/CONTACT-CLICK-STANDARD.md.
// Fires one GA4 `contact_click` event per tap on a phone / WhatsApp / email / SMS
// link, so RCA can count contact actions next to enquiries. Self-installs on
// import; guarded so it only ever attaches once.
//
// This is additive to, and independent of, this site's own bespoke conversion
// tracking in ../utils/gtag.ts (trackPhoneCall / trackEmailClick /
// trackWhatsAppClick, wired to real Google Ads conversion values). Both
// listeners fire on the same click without conflicting — do not remove either.

declare global {
  interface Window {
    __rcContactClick?: boolean;
  }
}

type ContactMethod = 'phone' | 'sms' | 'email' | 'whatsapp';

function methodFor(a: HTMLAnchorElement): ContactMethod | null {
  const explicit = a.getAttribute('data-contact');
  if (explicit === 'phone' || explicit === 'sms' || explicit === 'email' || explicit === 'whatsapp') {
    return explicit;
  }
  const h = (a.getAttribute('href') || '').toLowerCase();
  if (h.indexOf('tel:') === 0) return 'phone';
  if (h.indexOf('sms:') === 0) return 'sms';
  if (h.indexOf('mailto:') === 0) return 'email';
  if (h.indexOf('wa.me/') !== -1 || h.indexOf('api.whatsapp.com') !== -1 || h.indexOf('whatsapp://') === 0) {
    return 'whatsapp';
  }
  return null;
}

export function installContactClickTracking(): void {
  if (typeof document === 'undefined' || window.__rcContactClick) return;
  window.__rcContactClick = true;

  document.addEventListener(
    'click',
    (e: MouseEvent) => {
      const target = e.target as Element | null;
      const a = target && typeof target.closest === 'function'
        ? (target.closest('a[href],[data-contact]') as HTMLAnchorElement | null)
        : null;
      if (!a) return;

      const method = methodFor(a);
      if (!method) return;

      // No link_url: for tel:/mailto:/wa.me the href IS the phone number or
      // email address, which is personal data — GA4 gets method + page only.
      const params = {
        method,
        page_path: window.location.pathname,
      };

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'contact_click', params);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'contact_click', ...params });
      }
    },
    true
  );
}

installContactClickTracking();
